import React from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';

/**
 * TopMaterials — Tabelle der Top-5-Materialien nach Bestandswert.
 * Props:
 *   top_materialien  { source: string, data: Array<{ matnr, maktx, bestand, meins, wert }> }
 */
const TopMaterials = ({ top_materialien }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!top_materialien) return null;

  const data = Array.isArray(top_materialien.data) ? top_materialien.data : [];

  const sectionCls = isDarkMode
    ? 'bg-white/6 border border-white/12 rounded-2xl p-5'
    : 'bg-white border border-slate-200 rounded-2xl p-5 shadow-sm';

  const titleCls = isDarkMode
    ? 'text-sm font-semibold text-white/80 mb-4'
    : 'text-sm font-semibold text-slate-700 mb-4';

  const thCls = isDarkMode
    ? 'text-[11px] font-medium text-white/40 uppercase tracking-wide pb-2 text-left'
    : 'text-[11px] font-medium text-slate-400 uppercase tracking-wide pb-2 text-left';

  const tdCls = isDarkMode
    ? 'py-2 text-sm text-white/80'
    : 'py-2 text-sm text-slate-700';

  const trBorder = isDarkMode ? 'border-t border-white/8' : 'border-t border-slate-100';

  const formatEur = (val) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(val ?? 0);

  if (data.length === 0) {
    return (
      <div className={sectionCls}>
        <div className={titleCls}>{t('lm.dashTopMaterial')}</div>
        <p className={isDarkMode ? 'text-sm text-white/40' : 'text-sm text-slate-400'}>{t('lm.dashEmpty')}</p>
      </div>
    );
  }

  return (
    <div className={sectionCls}>
      <div className={titleCls}>{t('lm.dashTopMaterial')}</div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={thCls}>{t('lm.dashColMatnr')}</th>
              <th className={thCls}>{t('lm.dashColMaktx')}</th>
              <th className={`${thCls} text-right`}>{t('lm.dashColBestand')}</th>
              <th className={`${thCls} text-right`}>{t('lm.dashColWert')}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((m, i) => (
              <tr key={m.matnr || i} className={i > 0 ? trBorder : ''}>
                <td className={`${tdCls} font-mono text-xs`}>{m.matnr}</td>
                <td className={`${tdCls} max-w-[200px] truncate`}>{m.maktx}</td>
                <td className={`${tdCls} text-right whitespace-nowrap`}>
                  {formatQuantity(m.bestand, m.meins)} {m.meins}
                </td>
                <td className={`${tdCls} text-right whitespace-nowrap`}>{formatEur(m.wert)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopMaterials;
